import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { contentResource } from '@/features/content/api';
import { keys } from '@/lib/query/keys';
import { useLocale } from '@/lib/i18n/useLocale';
import { resolveI18n } from '@/lib/i18n/content';
import { Button } from '@/lib/ui/Button';
import { MonoValue } from '@/lib/ui/Typography';
import { ContentStatusPill } from '@/lib/ui/StatusPill';
import type { ContentItem } from '@/lib/api/types';

/**
 * The family's child products (§3.3). Surfacing the children right on the family page makes the
 * "products live inside a family" relationship obvious and gives the editor one click to add a
 * product already linked to this family (the new-product editor pre-fills the linkage from the
 * `?family=` slug). We filter client-side on `doc.family`/`doc.family_id` so the panel is correct
 * regardless of whether the admin list endpoint honours the facet param.
 */
export function FamilyProductsPanel({ familySlug, familyId }: { familySlug: string; familyId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const res = contentResource('products');

  const query = useQuery({
    queryKey: keys.content.list('products', { family: familySlug, per_page: 100 }),
    queryFn: () => res.list({ family: familySlug, per_page: 100 }),
    enabled: !!familySlug,
  });

  const products = useMemo(() => {
    const rows = query.data?.data ?? [];
    return rows.filter((p) => {
      const doc = (p.doc ?? {}) as Record<string, unknown>;
      return doc.family === familySlug || (familyId && doc.family_id === familyId);
    });
  }, [query.data, familySlug, familyId]);

  const addProduct = () =>
    navigate(`/content/products/new?family=${encodeURIComponent(familySlug)}&family_id=${encodeURIComponent(familyId)}`);

  return (
    <section className="flex flex-col gap-sm">
      <div className="flex items-center justify-between gap-sm">
        <div className="flex flex-col">
          <span className="eyebrow text-ink-mute">{t('families.productsHeading')}</span>
          <span className="text-small text-ink-mute">{t('families.productsHint')}</span>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={addProduct}>
          {t('families.addProduct')}
        </Button>
      </div>

      {query.isLoading ? (
        <p className="text-small text-ink-mute">{t('common.loading')}</p>
      ) : products.length === 0 ? (
        <p className="text-small text-ink-mute">{t('families.noProducts')}</p>
      ) : (
        <ul className="divide-y divide-hairline overflow-hidden rounded-md border-[0.5px] border-hairline">
          {products.map((p: ContentItem) => (
            <li key={p.id}>
              <Link
                to={`/content/products/${p.id}`}
                className="flex items-center justify-between gap-sm px-md py-sm text-small hover:bg-canvas-sand"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-ink-deep">{resolveI18n(p.title, locale)}</span>
                  <MonoValue className="truncate text-label-sm text-ink-mute">{p.slug}</MonoValue>
                </span>
                <ContentStatusPill status={p.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
